import { Store, select } from '@ngrx/store';
import { Observable, ReplaySubject } from 'rxjs';
import {
  distinctUntilChanged,
  withLatestFrom,
  filter,
  publishReplay,
  map,
  refCount
} from 'rxjs/operators';
import {
  GridOptions,
  ValueFormatterParams,
  TooltipParams
} from 'ag-grid-community';
import {
  numberComparer,
  ReusableStreamProperty,
  IssueOrderPeriod
} from '@ipreo/muni-shared';
import { GameDayModel } from '../../models/gameday-model';
import { BarChartLegend } from '../../shared/models/charts/chart-data-types';
import {
  generateMonitorOverviewItemByMaturity,
  generateMonitorOverviewItemByOrder,
  getColumnPosition
} from '../mappers/monitor-base-page.calculations';
import { GameDayCalculatedData } from '../../gameday/data/gameDay-data.calculation';
import { MonitorState } from '../../state/monitor/state';
import { GameDayRootReducerState } from '../../state/root.state';
import { getGamedayDealState, getMonitorState } from '../../state/data/reducer';
import { MonitorViewType, MonitorOverviewItem } from '../models/monitor';
import { ColumnDefinition } from '../../models/column';

export const RETAIL_TOTAL = 'Total Retail';
export const BASE_CATEGORIES: string[] = [
  GameDayModel.STOCK,
  GameDayModel.INSTITUTIONAL,
  GameDayModel.RETAIL
];

export abstract class MonitorBasePageService {
  @ReusableStreamProperty
  public readonly maturityIdsWithOrderPeriodTerminated: Observable<
    string[]
  > = this.store.pipe(
    select(getGamedayDealState),
    filter((deal: GameDayModel.Deal) => !!deal),
    map((deal: GameDayModel.Deal) => {
      const { isOpen } = IssueOrderPeriod.orderPeriodIsOpen(deal);

      if (isOpen && deal.maturities) {
        return deal.maturities
          .filter(
            (maturity: GameDayModel.Maturity) =>
              maturity && maturity.isOrderPeriodTerminated
          )
          .map((maturity: GameDayModel.Maturity) => maturity.id);
      } else {
        return [];
      }
    })
  );

  @ReusableStreamProperty
  public readonly selectedMaturityId$: Observable<string> = this.store.pipe(
    select(getMonitorState),
    map(({ selectedMaturityId }: MonitorState) => selectedMaturityId),
    distinctUntilChanged()
  );

  @ReusableStreamProperty
  public readonly viewType$: Observable<MonitorViewType> = this.store.pipe(
    select(getMonitorState),
    map(({ viewType }: MonitorState) => viewType),
    distinctUntilChanged()
  );

  public readonly defaultGridOptions: GridOptions = {
    getRowNodeId: ({ id }: { id: string }) => id,
    enableSorting: true,
    rowSelection: 'single',
    suppressMovableColumns: true,
    headerHeight: 28,
    rowHeight: 28
  };

  public readonly defaultColDefinition: ColumnDefinition = {
    label: 'Default',
    field: 'default',
    headerName: 'Default',
    position: 0,
    suppressFilter: true,
    suppressMenu: true,
    valueFormatter: (params: ValueFormatterParams): any => {
      return params.data
        ? params.data.formatted[params.colDef.field]
        : params.value;
    },
    tooltip: ({ value, valueFormatted }: TooltipParams) =>
      value || valueFormatted
  };

  private _dataSourceSubj: ReplaySubject<GameDayCalculatedData> = new ReplaySubject<GameDayCalculatedData>(
    1
  );

  abstract extractCategories(
    deal: GameDayModel.Deal,
    orders: GameDayModel.Order[]
  ): BarChartLegend[];

  abstract getCategoriesNames(
    categoriesMap: Map<string, BarChartLegend>,
    order: GameDayModel.Order,
    deal: GameDayModel.Deal
  ): string[];

  constructor(protected store: Store<GameDayRootReducerState>) {}

  public set dataSourceSubj(value: GameDayCalculatedData) {
    this._dataSourceSubj.next(value);
  }

  public getData(): Observable<GameDayCalculatedData> {
    return this._dataSourceSubj.asObservable().pipe(
      filter((state: GameDayCalculatedData) => !!state),
      distinctUntilChanged(),
      publishReplay(1),
      refCount()
    );
  }

  public getCategories(): Observable<BarChartLegend[]> {
    return this.getData().pipe(
      map(({ deal, orders }) => this.extractCategories(deal, orders)),
      distinctUntilChanged(),
      publishReplay(1),
      refCount()
    );
  }

  public getDataCombinedByMaturities(): Observable<MonitorOverviewItem[]> {
    return this.getData().pipe(
      map((data: GameDayCalculatedData) => {
        const { orders, deal, trancheGroups } = data;
        const categories = this.extractCategories(deal, orders);
        const categoriesMap = this.createCategoriesMap(categories);
        const groupedData = orders.reduce(
          (
            groupMap: Map<string, MonitorOverviewItem>,
            order: GameDayModel.Order
          ) => {
            let item: MonitorOverviewItem;
            const categoriesNames = this.getCategoriesNames(
              categoriesMap,
              order,
              deal
            );

            if (!groupMap.has(order.maturity.id)) {
              item = generateMonitorOverviewItemByOrder(
                categories,
                order,
                trancheGroups
              );
              groupMap.set(order.maturity.id, item);
            } else {
              item = groupMap.get(order.maturity.id);
              item.total += order.amount;
            }
            categoriesNames.forEach((name) => {
              item[name] = (<number>item[name] || 0) + order.amount;
            });

            return groupMap;
          },
          new Map<string, MonitorOverviewItem>()
        );

        deal.maturities.forEach((maturity) => {
          let item = groupedData.get(maturity.id);
          if (!item) {
            item = generateMonitorOverviewItemByMaturity(
              categories,
              maturity,
              trancheGroups
            );
            groupedData.set(maturity.id, item);
          }
          item.balance = item.amount - item.total;
        });

        return Array.from(groupedData.values());
      }),
      publishReplay(1),
      refCount()
    );
  }

  public calculateTotals(): Observable<MonitorOverviewItem> {
    const dataObservable = this.getDataCombinedByMaturities();
    const initDataObservable = this.getData();
    return dataObservable.pipe(
      withLatestFrom(initDataObservable, (data, initData) => {
        const { orders, deal } = initData;
        const categories = this.extractCategories(deal, orders);
        const result = this.getDefaultTotals(categories);
        data.forEach((item: MonitorOverviewItem) => {
          result.amount += item.amount;
          result.total += item.total;
          if (item.balance > 0) {
            result.balance += item.balance;
          }
          categories.forEach((category) => {
            result[category.name] =
              (result[category.name] as number) +
              (item[category.name] as number);
          });
        });

        result.subscription = result.total / result.amount;

        return result;
      })
    );
  }

  public getColumnDefinitions(
    columnDefinitions: ColumnDefinition[],
    categories: Observable<BarChartLegend[]>
  ): Observable<ColumnDefinition[]> {
    return this.getLegendColumnDefinitions(categories).pipe(
      map((definitions: ColumnDefinition[]) => {
        return columnDefinitions
          .concat(definitions)
          .sort(
            (prev: ColumnDefinition, next: ColumnDefinition) =>
              prev.position - next.position
          )
          .map((definition: ColumnDefinition) => ({
            ...this.defaultColDefinition,
            ...definition,
            headerTooltip: definition.headerName,
            headerClass: [
              `ag-header-gd-${definition.field.toLocaleLowerCase()}`,
              definition.type === 'numericColumn'
                ? 'ag-numeric-header'
                : 'ag-string-header'
            ],
            cellClass: [
              `ag-cell-gd-${definition.field.toLocaleLowerCase()}`,
              definition.type === 'numericColumn'
                ? 'ag-numeric-cell'
                : 'ag-string-cell'
            ]
          }));
      })
    );
  }

  private getLegendColumnDefinitions(
    categories: Observable<BarChartLegend[]>
  ): Observable<ColumnDefinition[]> {
    return categories.pipe(
      map((items) =>
        items.map((item: BarChartLegend, index: number) =>
          this.generateColumnDefinition(item, index)
        )
      )
    );
  }

  private generateColumnDefinition(
    item: BarChartLegend,
    index: number
  ): ColumnDefinition {
    const position = getColumnPosition(item, index);

    return {
      field: item.name,
      headerName: item.name,
      label: item.name,
      comparator: numberComparer,
      type: 'numericColumn',
      position
    };
  }

  private getDefaultTotals(categories: BarChartLegend[]): MonitorOverviewItem {
    const totals = {
      id: undefined,
      maturity: undefined,
      coupon: undefined,
      series: undefined,
      amount: 0,
      total: 0,
      balance: 0,
      subscription: 0
    };

    categories.forEach((category) => {
      totals[category.name] = 0;
    });

    return totals;
  }

  private createCategoriesMap(
    categories: BarChartLegend[]
  ): Map<string, BarChartLegend> {
    return new Map<string, BarChartLegend>(
      categories.map((category) => [category.id, category]) as any
    );
  }
}
